import { useContext } from 'react';
import cn from 'classnames';
import { PapiPremiumFolderData } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import FolderDescription from '../FolderDescription';
import { GallectionType } from '../../../../../../types/gallection';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import GallectionDeviationsTorpedo from '../../GallectionDeviationsTorpedo';
import FolderPaywall from '../../FolderPaywall';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import AnonPaginationBlocker from '@wix/da-shared-react/pkg/AnonPaginationBlocker';
import FolderBreadcrumbs from '../FolderBreadcrumbs/';
import { BiData, GalleryPurchaseBiEvent } from '@wix/da-bi/pkg/events';
import PagedPagination from '../../../_partials/StreamPagination/PagedPagination';
import usePagedFolderDeviations from '../hooks/usePagedFolderDeviations';

import s from './FolderPagedContent.scss';

export interface Props {
  folderId: number;
  className?: string;
}

function FolderPagedContent({ folderId, className }: Props) {
  const { isLoggedIn, contentFilter } = useContext(SessionContext);
  const isAnonGallectionNoPaginationFlagEnabled = useFlag('anon_gallection_no_pagination');
  const shouldShowAnonPaginationBlocker =
    isAnonGallectionNoPaginationFlagEnabled && !isLoggedIn && contentFilter !== 'mature';
  const {
    currentPage,
    deviations,
    folder,
    subfolder,
    gallectionType,
    isOwner,
    requestUrl,
    streamId,
    totalPages,
  } = usePagedFolderDeviations(folderId);
  if (!folder) {
    return null;
  }

  const { pointPrice, isPremiumLocked } = normalizePremiumFolderData(folder.premiumFolderData);
  const isCollection = gallectionType === GallectionType.COLLECTION;

  return (
    <OverlayContextProvider
      value={{
        showAuthorTooltip: isCollection,
        showThumbStatusIndicators: false,
      }}
    >
      <div className={className}>
        <ErrorBoundary debugComponent="FolderPagedContent">
          <FolderDescription folder={folder} className={s['folder-description']} />
          {subfolder && <FolderBreadcrumbs />}

          {!isPremiumLocked || isOwner ? (
            deviations.length > 0 && (
              <div>
                <BiLoggerContextProvider
                  value={{
                    section_typeid: gallectionTypeToResourceType(gallectionType),
                    sectionid: folder.folderId,
                    authorid: folder.owner.useridUuid,
                  }}
                >
                  <GallectionDeviationsTorpedo
                    streamId={streamId}
                    gallectionType={gallectionType}
                    deviations={deviations}
                  />
                </BiLoggerContextProvider>
              </div>
            )
          ) : (
            <FolderPaywall
              premiumFolderData={{
                ...(folder.premiumFolderData as PapiPremiumFolderData),
                galleryUrl: Url.userGalleryLink({
                  username: folder.owner.username,
                  gallectionType: 'gallery',
                  folderId: folder.folderId,
                  folderName: folder.name,
                }),
              }}
              folder={folder}
              biData={BiData<GalleryPurchaseBiEvent>({
                evid: 361,
                points: pointPrice,
                typeid: gallectionTypeToResourceType(gallectionType) ?? 0,
                itemid: folder.folderId,
                galleryid: folder.folderId,
                authorid: folder.owner.useridUuid,
              })}
            />
          )}

          {!isPremiumLocked && totalPages > 1 && (
            <div
              className={cn(
                s['button-wrapper'],
                shouldShowAnonPaginationBlocker && s['anon-pagination-blocker']
              )}
            >
              {shouldShowAnonPaginationBlocker && (
                <AnonPaginationBlocker joinpoint="gallection-anon-pagination-blocker" />
              )}
              <PagedPagination
                currentPage={currentPage}
                paginationClassName={cn(
                  shouldShowAnonPaginationBlocker && s['pagination-blocked-for-anon']
                )}
                requestUrl={requestUrl}
                showPageNumbers={!!totalPages}
                totalPages={totalPages}
              />
            </div>
          )}
        </ErrorBoundary>
      </div>
    </OverlayContextProvider>
  );
}

FolderPagedContent.displayName = 'FolderPagedContent';

export default FolderPagedContent;
